/**
 * The decisions behind Reveal and CardCarousel, kept free of React and the DOM
 * so they can be pinned by plain unit tests (packages/ui/test). The components
 * are thin wiring around these.
 */

/** The query every motion decision in this package reads. */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** True when the visitor has asked for less movement. False on the server. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * Should a Reveal hide itself (ready to fade in) once the page is running?
 *
 * The prerendered HTML always shows everything — this is only ever asked after
 * hydration. The answer is no whenever hiding could cost the reader anything:
 * no IntersectionObserver to bring it back, a reduced-motion preference, or
 * any part of the element already on screen (hiding what someone is looking
 * at, then fading it back, is a flash, not an entrance). Only content wholly
 * below the fold is hidden.
 */
export function shouldHideForReveal(input: {
  /** element's top edge, relative to the viewport (getBoundingClientRect().top) */
  top: number;
  /** window.innerHeight */
  viewportHeight: number;
  reducedMotion: boolean;
  hasObserver: boolean;
}): boolean {
  if (!input.hasObserver || input.reducedMotion) return false;
  return input.top >= input.viewportHeight;
}

/** Length of a Reveal entrance. */
export const REVEAL_DURATION_MS = 500;

/**
 * Hands out stagger slots to Reveals that come into view together.
 *
 * Each Reveal in a RevealGroup claims a slot at the moment it is revealed;
 * claims made within `windowMs` of each other count up (0, 1, 2 …), and the
 * count starts again after a quiet spell. So a row of cards scrolling in
 * together cascades, while a card that arrives on its own a screen later
 * starts at once instead of waiting behind every card above it.
 */
export function createStaggerClock(stagger: number, now: () => number, windowMs = 120) {
  let last = -Infinity;
  let index = 0;
  return {
    /** The extra delay (ms) for the Reveal claiming now. */
    claim(): number {
      const t = now();
      index = t - last <= windowMs ? index + 1 : 0;
      last = t;
      return index * stagger;
    },
  };
}

/** Accessible name for one slide: "2 of 5". */
export function slideLabel(index: number, total: number): string {
  return `${index + 1} of ${total}`;
}

/**
 * Classes that hide CardCarousel's prev/next controls where the default layout
 * (1 · 2 from sm · 3 from lg) already fits every card — decided in CSS so the
 * prerendered page is right before any script runs. Once Embla is measuring,
 * its own "can scroll" answer takes over; this only avoids a flash of buttons
 * that would then disappear.
 */
export function controlsFitClasses(count: number): string {
  if (count <= 1) return "hidden";
  if (count === 2) return "sm:hidden";
  if (count === 3) return "lg:hidden";
  return "";
}
